import {
  ChangeDetectionStrategy,
  Component,
  computed,
  HostListener,
  inject,
  input,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { DialogHeaderComponent, FaIconComponent } from '@akostm/orbit-ui';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { AssetsPreviewDialogData } from '../../../../interfaces';
import { Message, MessageKind } from '@clientwire/api';
import {
  faChevronLeft,
  faChevronRight,
  faFileAudio,
  faVideo,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { NgClass } from '@angular/common';

@Component({
  selector: 'shared-assets-preview-dialog',
  standalone: true,
  imports: [DialogHeaderComponent, FaIconComponent, NgClass],
  templateUrl: './assets-preview-dialog.component.html',
  styleUrl: './assets-preview-dialog.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssetsPreviewDialogComponent implements OnInit {
  data: AssetsPreviewDialogData = inject(DIALOG_DATA);

  readonly #dialogRef = inject(DialogRef);
  readonly messageKindEnum = MessageKind;
  readonly audioIcon = faFileAudio;
  readonly videoIcon = faVideo;
  readonly leftIcon = faChevronLeft;
  readonly rightIcon = faChevronRight;
  readonly closeIcon = faXmark;
  readonly #translateService = inject(TranslateService);

  title = input<string>(this.#translateService.instant('SHARED.DIALOGS.ASSETS_PREVIEW.TITLE'));
  closeLabel = input<string>(
    this.#translateService.instant('SHARED.DIALOGS.ASSETS_PREVIEW.CLOSE_LABEL')
  );
  assetsMessages = signal<Message[]>([]);
  selectedMessage = signal<Message | null>(null);
  selectedMessageIndex = computed(() => {
    if (!this.selectedMessage()) return -1;
    return this.assetsMessages().findIndex((m) => m.id === this.selectedMessage()?.id);
  });

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key === 'ArrowLeft') {
      this.onLeftClick();
    } else if (event.key === 'ArrowRight') {
      this.onRightClick();
    }
  }

  ngOnInit(): void {
    this.assetsMessages.set(
      this.data.messages.filter(
        (message) =>
          message.kind === MessageKind.Image ||
          message.kind === MessageKind.Video ||
          message.kind === MessageKind.Audio
      )
    );
    this.selectedMessage.set(this.data.selectedMessage ?? null);
  }

  selectMessage(message: Message) {
    this.selectedMessage.set(message);
  }

  onLeftClick() {
    console.log(this.selectedMessageIndex());
    if (this.selectedMessage()) {
      const lastMessageIndex = this.assetsMessages().length - 1;
      if (this.selectedMessageIndex() !== -1) {
        if (this.selectedMessageIndex() > 0) {
          this.selectedMessage.set(this.assetsMessages()[this.selectedMessageIndex() - 1] ?? null);
        } else {
          this.selectedMessage.set(this.assetsMessages()[lastMessageIndex] ?? null);
        }
      }
    }
  }

  onRightClick() {
    if (this.selectedMessage()) {
      const lastMessageIndex = this.assetsMessages().length - 1;
      if (this.selectedMessageIndex() !== -1) {
        if (this.selectedMessageIndex() < lastMessageIndex) {
          this.selectedMessage.set(this.assetsMessages()[this.selectedMessageIndex() + 1] ?? null);
        } else {
          this.selectedMessage.set(this.assetsMessages()[0] ?? null);
        }
      }
    }
  }

  closeDialog(): void {
    this.#dialogRef.close();
  }

  onCloseKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.closeDialog();
    }
  }

  onLeftKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onLeftClick();
    }
  }

  onRightKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onRightClick();
    }
  }

  onSelectMessageKeyDown(event: KeyboardEvent, message: Message): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.selectMessage(message);
    }
  }
}
